import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Well, Drawer, HighlightedTextArea } from '@pushly/aqe/lib/components'
import { EditOutlined } from '@ant-design/icons'
import { Flex, Skeleton, Spin, Splitter, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import * as randomstring from 'randomstring'
import { AppService, AiGenerationDirectiveService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { simpleNotification } from '../../_utils/utils'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { BrandVoiceSummary } from './brand-voice-summary'
import {
    AiCompilingIndicator,
    BrandVoiceDecomposition,
    BrandVoiceSummaryChips,
    CompileState,
    BrandVoiceTierKey,
    LINKED_ELEMENT_SELECTOR,
} from './brand-voice-decomposition'
import { findAllTierSpans, dedupeSpansForRender, reconcileSpans, ALL_TIER_HIGHLIGHT_STYLES } from './text-highlight'
import { applyReplacement, dimensionFor, groupSuggestionsByItem } from './promotion-suggestions'
import { locateSpan } from './span-locator'
import { PromotionModal, type IPromotionTarget } from './promotion-modal'
import { applyPromotionToCompiled, enforcedLimitsByAttribute } from './promotion-optimistic'
import { BrandVoiceAuthoringHelp } from './brand-voice-authoring-help'

const PLACEHOLDER =
    'e.g. Write like a trusted editor. Never use exclamation points. Keep titles under 60 characters. Favor curiosity over hype.'

interface IBrandVoiceEditor {
    domainId: number
    directive: AiGenerationDirective | null
    visible: boolean
    onClose: (e: any) => void
    onSaved: (directive: AiGenerationDirective) => void
}

const BrandVoiceEditor: FC<IBrandVoiceEditor> = (props) => {
    const appSvc = useService(AppService)
    const directiveSvc = useService(AiGenerationDirectiveService)

    // Textareas normalize \r\n/\r to \n internally for both their rendered value and
    // selectionStart/layout metrics — but the raw server body can still contain \r\n. If `body`
    // state kept those \r characters, every span offset computed against it would drift right
    // by one character per line break that precedes the match (the browser already dropped
    // that \r when laying the text out), compounding across paragraphs. Normalizing at the one
    // point raw server text enters state keeps our offsets and the browser's in agreement.
    const initialBody = (props.directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const initialCompiled = props.directive?.active_version?.compiled_rules ?? null

    const [body, setBody] = useState<string>(initialBody)
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [compileState, setCompileState] = useState<CompileState>(initialCompiled ? 'done' : 'idle')
    const [compiled, setCompiled] = useState<CompiledDirective | null>(initialCompiled)
    // Promotion suggestions for whatever `compiled` currently holds. Purely additive
    // to the editor: they are fetched after a compile lands, and a failure leaves them empty
    // rather than surfacing an error — losing a hint must never look like losing the analysis.
    const [suggestions, setSuggestions] = useState<DirectivePromotionSuggestion[]>([])
    // The domain's own measured p90 per dimension, carried alongside the suggestions so the
    // confirm step can retarget one onto another field or unit without another round trip.
    const [contentStats, setContentStats] = useState<{
        p90ByAttribute: Record<string, number>
        p50ByAttribute: Record<string, number>
        sampleSize: number
    }>({ p90ByAttribute: {}, p50ByAttribute: {}, sampleSize: 0 })
    // The statement whose promotion is being confirmed, or null. Null closes the modal.
    const [promotionTarget, setPromotionTarget] = useState<IPromotionTarget | null>(null)
    /**
     * A compile running over content that is ALREADY on screen. `compileState === 'compiling'`
     * is the other case — a first compile, with nothing to preserve — and that one replaces the
     * panel entirely.
     *
     *   'blocking'  the author clicked Analyze and is waiting for the answer. The rules stay
     *               visible under a loading overlay and the editor is locked, so the text cannot
     *               drift out from under a result that is about to describe it.
     *   'quiet'     WE started this, after a promotion. The author did not ask and may well keep
     *               working, so nothing is taken away — just a line saying a check is running.
     *
     * The distinction is who is waiting. Locking someone out of their own text is only
     * defensible when they asked for the thing they are being made to wait for.
     */
    const [busyMode, setBusyMode] = useState<'blocking' | 'quiet' | null>(null)

    // Jump to a tier's section from the strip above the panel. Queried by `data-bvd-tier` rather
    // than held as four refs: the sections are rendered by the decomposition component, and
    // threading refs out through it would couple it to how this drawer happens to lay them out.
    const decompositionRef = useRef<HTMLDivElement>(null)
    const scrollToTier = (tier: BrandVoiceTierKey) => {
        decompositionRef.current
            ?.querySelector(`[data-bvd-tier="${tier}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    // The body whatever `compiled` currently holds actually reflects (a persisted result on
    // load, or the last successful recompile) — lets the effect below tell "the author edited
    // the text" apart from "the component re-rendered with the same text", so opening the
    // drawer on a directive with a saved decomposition never fires a redundant compile call.
    const lastCompiledBodyRef = useRef<string | undefined>(initialCompiled ? initialBody : undefined)

    // Textarea <-> rule-row highlight linking. Spans come straight off `compiled`'s
    // own attribution (each item's `sources`, resolved server-side against the exact body that
    // produced it — see text-highlight.ts) rather than a client-side re-search, so they only
    // change when a new `compiled` result actually lands, not on every keystroke. Between an
    // edit and the next Analyze click landing, spans stay pinned to the last-compiled body's
    // positions rather than the text currently on screen — expected, since nothing downstream
    // (the rule rows) has changed either until that compile completes.
    // Hover and selection are separate states. Hover is a transient preview that follows the
    // pointer; a click PINS a rule so its source text stays lit while the author reads it,
    // scrolls, or moves the mouse away. Previously `onClick` only set the same key `onMouseEnter`
    // did, so leaving the row immediately cleared it — a click bought a scroll and then threw the
    // highlight away, which made the deliberate gesture weaker than the incidental one.
    const [pinnedSpanKey, setPinnedSpanKey] = useState<string | null>(null)
    const [hoveredSpanKey, setHoveredSpanKey] = useState<string | null>(null)
    // A pin outranks hover: once something is pinned, drifting the pointer over other rows must
    // not silently repaint the selection the author just made.
    const activeSpanKey = pinnedSpanKey ?? hoveredSpanKey
    // All four tiers, not just Checked/Needs-review. Guaranteed was excluded while its rows
    // were unaddressable — the decomposition renders them through PQueryBuilder, which had no
    // per-row hook — so handing the textarea guaranteed spans would have highlighted text no
    // row could link back to. aqe's `rowProps` seam closes that.
    const compiledSpans = useMemo(() => (compiled ? findAllTierSpans(compiled) : []), [compiled])

    // Reconciled against the text as it stands right now, on every keystroke. Spans are offsets
    // into the body that was COMPILED, and nothing errors when the author edits past them — the
    // numbers stay valid and simply stop meaning anything, so the marks slide out of register
    // and start painting half-words. Replacing the whole directive is the extreme case; a line
    // typed above the first rule is the everyday one.
    //
    // The array identity is held stable while the resolved ranges are unchanged (the common case
    // when typing at the end, where every span stays verified where it is).
    // `HighlightedTextArea` rebuilds its entire DOM whenever `spans` changes identity, and doing
    // that per keystroke is what its own docstring warns against — it saves and restores the
    // caret by hand around each rebuild.
    const spansRef = useRef<ReturnType<typeof findAllTierSpans>>([])
    const spansSignatureRef = useRef('')
    const spans = useMemo(() => {
        const next = reconcileSpans(compiledSpans, body, lastCompiledBodyRef.current)
        const signature = next.map((s) => `${s.key}:${s.start}:${s.end}`).join('|')
        if (signature !== spansSignatureRef.current) {
            spansSignatureRef.current = signature
            spansRef.current = next
        }
        return spansRef.current
    }, [compiledSpans, body])
    const matchedSpanKeys = useMemo(() => new Set(spans.map((s) => s.key)), [spans])
    // Memoized for the same reason `spans` above is: `dedupeSpansForRender` filters, and
    // `filter` allocates a new array even when it removes nothing — passing the call inline
    // would hand HighlightedTextArea a fresh identity every render and throw away the
    // stabilization the signature check exists to provide.
    const renderSpans = useMemo(() => dedupeSpansForRender(spans), [spans])

    // A click (not hover) also requests a scroll — `nonce` forces the request through even
    // when clicking the SAME row twice in a row (the textarea may have since been scrolled
    // away by the author's own typing/scrolling), which a bare key alone wouldn't re-trigger
    // since it wouldn't be seen as a change by HighlightedTextArea's effect dependency.
    const scrollNonceRef = useRef(0)
    const [scrollToSpan, setScrollToSpan] = useState<{ key: string; nonce: number } | null>(null)
    // Click toggles: clicking the pinned rule again releases it, so the same gesture that
    // focuses also dismisses without hunting for a separate control. Re-clicking a DIFFERENT
    // rule moves the pin straight there.
    const handleSpanClick = (key: string) => {
        setPinnedSpanKey((current) => (current === key ? null : key))
        if (pinnedSpanKey !== key) setScrollToSpan({ key, nonce: ++scrollNonceRef.current })
    }

    // Release a pin that a recompile invalidated. Span keys are positional within their tier
    // (`checked-3`), so after a fresh Analyze the same key can be a DIFFERENT rule or no rule at
    // all — leaving it pinned would silently point the author at the wrong sentence.
    useEffect(() => {
        if (pinnedSpanKey && !matchedSpanKeys.has(pinnedSpanKey)) setPinnedSpanKey(null)
    }, [matchedSpanKeys, pinnedSpanKey])

    // Clicking away releases the pin. Deliberately NOT Escape: this lives inside a Drawer, and
    // Escape is already the Drawer's own close — stealing it would either shut the whole editor
    // while the author was only trying to clear a highlight, or swallow the close they wanted.
    //
    // "Away" means anything that isn't part of the selection itself: a linked row (any tier) or
    // a highlighted mark in the text. Clicking a DIFFERENT linked row is not a dismissal — that
    // row's own handler moves the pin, and this listener sees the click landed inside a linked
    // element and leaves it alone. Bound on the document, so it also catches clicks on the
    // Drawer chrome outside either panel.
    useEffect(() => {
        if (!pinnedSpanKey) return
        const onDocumentClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null
            if (target?.closest?.(LINKED_ELEMENT_SELECTOR)) return
            setPinnedSpanKey(null)
        }
        document.addEventListener('click', onDocumentClick)
        return () => document.removeEventListener('click', onDocumentClick)
    }, [pinnedSpanKey])

    // The most recently ISSUED compile request's text — not the most recently RESOLVED one.
    // Debouncing only cancels a pending timer; it can't cancel a request already in flight, so
    // two compiles can legitimately overlap (e.g. the author keeps editing while a slow compile
    // of a long body is still running). Without this guard, whichever response happens to
    // resolve last wins regardless of which was issued last — a stale, shorter-body response
    // arriving after a newer one would silently overwrite it with rule text and highlight
    // offsets that no longer match what's on screen.
    const latestRequestedTextRef = useRef<string>('')

    // Read inside `runCompile` instead of closing over `compiled`, which would rebuild the
    // callback on every compile and re-arm the debounced background compile with a stale one.
    const compiledRef = useRef<CompiledDirective | null>(initialCompiled)
    useEffect(() => {
        compiledRef.current = compiled
    }, [compiled])

    const runCompile = useCallback(
        async (text: string, opts: { mode?: 'blocking' | 'quiet' } = {}) => {
            latestRequestedTextRef.current = text

            if (!text.trim()) {
                setCompiled(null)
                setCompileState('idle')
                setSuggestions([])
                lastCompiledBodyRef.current = undefined
                return
            }
            // With something already on screen, `compileState` stays 'done' so the decomposition
            // keeps rendering and the mode decides how the wait is presented. Submit is not
            // weakened by that: it also requires the text on screen to equal
            // `lastCompiledBodyRef`, which only a FINISHED compile sets.
            if (compiledRef.current) setBusyMode(opts.mode ?? 'blocking')
            else setCompileState('compiling')
            // Dropped up front, not on the response: they describe the OLD decomposition, and
            // leaving them on screen through a recompile would invite applying a rewrite whose
            // offsets belong to text that is already being replaced.
            setSuggestions([])
            setPromotionTarget(null)
            const res = await directiveSvc.compilePreview(props.domainId, text)

            // A newer request has since been issued (and will supersede this one on its own
            // resolution) — applying this now-stale result would only reintroduce the race.
            if (latestRequestedTextRef.current !== text) return

            setBusyMode(null)

            if (res.ok && res.data) {
                setCompiled(res.data)
                setCompileState('done')
                lastCompiledBodyRef.current = text

                // Deliberately NOT awaited into the compile's own state transitions: the
                // decomposition is the answer the author asked for and must paint as soon as it
                // lands. Suggestions are an extra that arrives a moment later if it arrives.
                const suggestionRes = await directiveSvc.suggestPromotions(props.domainId, res.data)
                if (latestRequestedTextRef.current !== text) return
                if (suggestionRes.ok && suggestionRes.data) {
                    setSuggestions(suggestionRes.data.suggestions ?? [])
                    setContentStats({
                        p90ByAttribute: suggestionRes.data.p90_by_attribute ?? {},
                        p50ByAttribute: suggestionRes.data.p50_by_attribute ?? {},
                        sampleSize: suggestionRes.data.sample_size ?? 0,
                    })
                }
            } else if (compiledRef.current) {
                // A failed compile must not replace content the author is reading with an error
                // panel — the previous rules are still true for the previous text.
                // `lastCompiledBodyRef` stays behind, so `isStale` lights the Analyze button and
                // the hint below the textarea already says the rules predate the current text.
                setCompileState('done')
            } else {
                setCompileState('error')
            }
        },
        [directiveSvc, props.domainId],
    )

    // Grouped by the statement each is about — an ambiguous-subject statement produces one
    // suggestion per plausible field, and they are alternatives the author chooses between
    // rather than a list of edits to make.
    const suggestionsByItem = useMemo(() => groupSuggestionsByItem(suggestions), [suggestions])

    // Limits the author has already written, by binding attribute — so the confirm step can
    // refuse to aim a promotion at a dimension their own text already sets.
    const enforcedByAttribute = useMemo(() => enforcedLimitsByAttribute(compiled), [compiled])

    /**
     * Rewrites the author's own text, which is the ONLY way a promotion can take effect:
     * compiled rules are re-derived from the body on every compile, so a binding introduced any
     * other way would evaporate on the next Analyze.
     *
     * Three things happen together, and the order matters to how it feels:
     *   1. the sentence is spliced into the text
     *   2. the decomposition is updated LOCALLY so the rule appears under Guaranteed at once
     *   3. the real compile is started in the background, and replaces (2) when it lands
     *
     * (2) is a prediction, not a result — see `applyPromotionToCompiled` for why it can be an
     * accurate one. It exists because the alternative was asking the author to click Analyze and
     * watch a spinner to see the outcome of a change they had already confirmed.
     *
     * (3) is NOT an extra compile. `compileAndPersist` reuses the same Redis pre-warm keyed on
     * body text, so this is precisely the compile that Save would otherwise have run — started
     * earlier, while the author is still reading, instead of at the moment they press Save.
     */
    const handleConfirmPromotion = (
        replaces: DirectivePromotionSuggestion['replaces'],
        sentence: string,
        dimension: { field: 'title' | 'body'; unit: 'characters' | 'words'; value: number },
    ) => {
        // Offsets were computed against the text Analyze compiled, which is `body.trim()` — the
        // editor's own state keeps the author's surrounding whitespace, so the compiled body is
        // handed over to re-base them rather than left to a quote search to paper over.
        const located = locateSpan(body, replaces, lastCompiledBodyRef.current)
        const next = located && applyReplacement(body, replaces, sentence, lastCompiledBodyRef.current)
        if (!located || next == null) {
            // The modal disables its confirm when the span can't be located, so this is only
            // reachable if the text moved between opening it and confirming.
            simpleNotification('warning', 'That text has changed — click Analyze and try again.')
            setPromotionTarget(null)
            return
        }

        const itemText = promotionTarget?.suggestions[0].item_text
        setBody(next)
        setIsDirty(true)
        setPromotionTarget(null)

        // Where the new sentence now sits — known exactly, since we just placed it. The
        // Guaranteed row it produces therefore links back to its own source text immediately,
        // rather than waiting for the compiler to re-locate a quote it wrote itself.
        const sentenceSpan = { start: located.start, end: located.start + sentence.length, quote: sentence }
        if (compiled && itemText) {
            setCompiled(
                applyPromotionToCompiled(
                    compiled,
                    itemText,
                    dimensionFor(dimension.field, dimension.unit),
                    dimension.value,
                    sentenceSpan,
                ),
            )
        }

        // Every remaining suggestion's offsets were computed against the body as it was BEFORE
        // this splice, so they have all just shifted. `locateSpan` re-finds them by quote,
        // but only the ones for OTHER statements — the alternatives that shared this statement
        // are now moot, since the sentence they were about no longer exists.
        setSuggestions((current) => current.filter((s) => s.item_text !== itemText))

        scheduleBackgroundCompile(next.trim())
    }

    /**
     * Starts the real compile behind the local prediction, coalescing a run of promotions into
     * one request.
     *
     * Promoting three suggestions in a row is a normal thing to do, and firing a ten-second
     * compile after each would burn two of them on text that was superseded before they
     * returned. The delay is short enough to be invisible when only one promotion happens.
     */
    const backgroundCompileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scheduleBackgroundCompile = (text: string) => {
        if (backgroundCompileTimerRef.current) clearTimeout(backgroundCompileTimerRef.current)
        backgroundCompileTimerRef.current = setTimeout(() => runCompile(text, { mode: 'quiet' }), 1200)
    }

    useEffect(() => {
        return () => {
            if (backgroundCompileTimerRef.current) clearTimeout(backgroundCompileTimerRef.current)
        }
    }, [])

    // Explicit "Analyze" trigger, not a debounced auto-compile: firing a compile
    // after every pause in typing meant any hesitation mid-sentence kicked off a request
    // against still-incomplete text — annoying in itself, and the source of most of the
    // "stale result overwritten by a slower one" races `latestRequestedTextRef` above exists
    // to guard against. The author decides when the text is actually ready to analyze.
    // `isStale` is what the Analyze button and decomposition below key off of to signal that
    // the rules on screen no longer reflect what's currently written.
    // Compared against `body.trim()`, not raw `body`: `runCompile` is always called with
    // trimmed text below (see its call sites), matching exactly what `handleSubmit` persists
    // as the directive's body. Comparing untrimmed `body` here would flag surrounding
    // whitespace alone as "changed since last analyzed", and — more importantly — letting
    // Analyze and Submit ever compile two DIFFERENT strings for the same author-visible text
    // is exactly what broke `compileAndPersist`'s equality check server-side: its `sources`
    // spans are absolute character offsets into whatever exact string was compiled, so even a
    // stray trailing newline shifts every offset and makes an otherwise-identical recompile
    // fail to match.
    const isStale = body.trim() !== '' && body.trim() !== lastCompiledBodyRef.current

    const handleSubmit = async () => {
        const trimmed = body.trim()
        if (!trimmed) {
            simpleNotification('error', 'Brand Voice cannot be empty')
            return
        }
        if (!compiled) {
            // `disableSubmit` below should prevent reaching here without a completed, current
            // compile — defensive guard, not the primary gate.
            simpleNotification('error', 'Please wait for the Brand Voice to finish analyzing before saving')
            return
        }

        setSaving(true)
        try {
            const res = props.directive
                ? await directiveSvc.update(
                      props.domainId,
                      props.directive.id,
                      UpdateAiGenerationDirectiveRequest.from({ body: trimmed }),
                  )
                : await directiveSvc.create(
                      props.domainId,
                      CreateAiGenerationDirectiveRequest.from({
                          role: 'brand_voice',
                          name: 'Brand Voice',
                          body: trimmed,
                      }),
                  )

            if (!res.ok || !res.data) return

            // Persist the EXACT decomposition just displayed and approved (`compiled`) — the
            // server independently recompiles and only writes it if its own fresh result
            // matches, so the next time this directive opens, its saved rules are there to
            // show immediately. Reuses the Redis pre-warm from the Analyze click above (same
            // exact text, since Submit is disabled until that Analyze result is current), so
            // this is typically instant.
            const compiledRes = await directiveSvc.compileAndPersist(props.domainId, res.data.id, compiled)

            if (compiledRes.ok && compiledRes.data) {
                simpleNotification('success', 'Brand Voice was successfully updated')
                props.onSaved(compiledRes.data)
            } else {
                // The server's fresh recompile no longer matched what was reviewed (an expired
                // preview cache, a compiler change, or a stale review) — the text itself saved,
                // but the reviewed decomposition was NOT persisted rather than silently
                // persisting something different from what was approved.
                simpleNotification(
                    'warning',
                    "Brand Voice text was saved, but its rules changed since you last reviewed them. Reopen to re-check before they're enforced.",
                )
                props.onSaved(res.data)
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-brand-voice-editor')}
            title="Update Brand Voice"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            // Never enabled mid-compile or against stale/errored text — Submit only becomes
            // available once `compiled` reflects a successfully-finished compile of the exact
            // text on screen, so what gets persisted is guaranteed to be what was reviewed.
            //
            // Deliberately NOT gated on `saving`: aqe's SubmitButton renders a structurally
            // different element (a bare span vs. a Tooltip-wrapped one) depending on whether
            // it's disabled, so flipping `disableSubmit` true as a synchronous side effect of
            // the click (which `setSaving(true)` inside handleSubmit is) remounts the inner
            // Button mid-click and discards the loading state it had just set — the spinner
            // never gets a chance to paint. Re-entrancy during the save is already handled by
            // aqe's own Button (it no-ops a click while its internal `isLoading` is true), so
            // this doesn't need its own guard against a double-submit.
            disableSubmit={!isDirty || compileState !== 'done' || body.trim() !== lastCompiledBodyRef.current}
            onClose={props.onClose}
            open={props.visible}
            size={1280}
        >
            <Flex vertical gap={16} className={getClassNames('brand-voice-editor-body')}>
                {/* Says what a Brand Voice IS, where it applies, and what it does not touch.
                    The previous line ("free-text guidance, split into rules we can guarantee and
                    style we apply best-effort") described our own MECHANISM to someone who had
                    not yet been told there was a mechanism — tiers are the answer to a question
                    the author has not asked on arrival.
                    Every claim here is checked against `PushlyAiService.applyEffectiveBrandVoice`:
                    it is applied by default to every generate call for the domain, it is
                    server-authored (a client cannot override it), the Guaranteed tier is enforced
                    locally after generation, and it sits ONLY on the generation path — so a
                    notification written by hand never passes through it. */}
                <Typography.Text className={getClassNames('brand-voice-intro')}>
                    Your Brand Voice is how you describe your writing style, in your own words. It applies to every
                    notification the AI generates for this domain — rules we can measure are enforced on each one, and
                    the rest steers how it writes. It never changes notifications you write yourself.
                </Typography.Text>

                {/* A direct viewport-relative height, not a percentage chained up through
                    Flex -> the antd Drawer body -> the Drawer's own panel sizing — that chain
                    depends on several nested elements each correctly resolving a definite
                    height for a `height: 100%` below them to mean anything, and in practice it
                    collapsed to the textarea's tiny intrinsic default instead of filling the
                    drawer. A fixed calc against the viewport sidesteps all of that: 240px
                    accounts for the drawer header, its body padding, and the intro paragraph
                    above this row: same on-screen effect, without depending on ancestors we
                    don't control resolving heights the way we'd expect. `Splitter.Panel`
                    children stretch to fill this explicit height automatically (antd's own
                    cross-axis stretch, not something we have to wire up). */}
                <Splitter style={{ height: 'calc(100vh - 240px)', minHeight: 320 }}>
                    <Splitter.Panel style={{ minWidth: '33%' }}>
                        <div
                            style={{
                                margin: '0 12px',
                                height: '100%',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                            }}
                        >
                            <Flex align="center" justify="space-between">
                                <Typography.Text strong>Your Brand Voice</Typography.Text>
                                <Button
                                    size="small"
                                    type={isStale ? 'primary' : 'default'}
                                    loading={compileState === 'compiling' || !!busyMode}
                                    // Also blocked while saving — a click here mid-save would
                                    // kick off a recompile against text the save is actively
                                    // persisting, whose result could land after the save
                                    // finishes and make `compiled` disagree with what was just
                                    // written.
                                    disabled={compileState === 'compiling' || !!busyMode || !isStale || saving}
                                    // Trimmed to match exactly what handleSubmit persists as
                                    // the directive's body — see `isStale`'s docstring above.
                                    //
                                    // Re-analyzing runs in the BACKGROUND whenever there is
                                    // already a decomposition on screen: replacing a full panel of
                                    // rules with a spinner for ten seconds throws away everything
                                    // the author was reading, and the previous result stays true
                                    // for the previous text the whole time it is running. Only a
                                    // first-ever compile has nothing to keep.
                                    onClick={() => runCompile(body.trim())}
                                >
                                    Analyze
                                </Button>
                            </Flex>
                            {/* Above the field, not below it with the result hint: this is about
                                what to type, and it is worth reading before typing. Open on
                                arrival only for an empty directive — a blank page is exactly when
                                it helps and exactly when it costs nothing. `initialBody` rather
                                than live `body`, so it does not collapse out from under someone
                                as they type their first sentence. */}
                            <BrandVoiceAuthoringHelp defaultOpen={initialBody.trim() === ''} />
                            <HighlightedTextArea
                                // Locked while the author waits on a compile THEY asked for —
                                // editing mid-compile means the result lands describing text that
                                // no longer exists. Covers the first compile too, which has no
                                // previous decomposition to keep and so never sets `busyMode`.
                                // A quiet post-promotion refresh never locks: nobody asked for it
                                // and they may still be working.
                                disabled={compileState === 'compiling' || busyMode === 'blocking'}
                                value={body}
                                onChange={(value) => {
                                    // Defensive: the DOM's textarea sanitization already normalizes
                                    // typed/pasted input to \n, but keep state consistent regardless
                                    // of how a future caller might set this value.
                                    setBody(value.replace(/\r\n?/g, '\n'))
                                    setIsDirty(true)
                                }}
                                placeholder={PLACEHOLDER}
                                spans={renderSpans}
                                activeSpanKey={activeSpanKey}
                                onSpanHover={setHoveredSpanKey}
                                scrollToSpan={scrollToSpan}
                                tierStyles={ALL_TIER_HIGHLIGHT_STYLES}
                            />
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {/* Named the two tiers that could be hovered when only those two
                                    were wired. All four highlight now, and the sentence had grown
                                    long enough that the useful half — that the link works in both
                                    directions — was reached last. */}
                                {isStale
                                    ? 'The rules on the right are from an earlier version of this text — click Analyze to refresh them.'
                                    : 'Hover or click a rule to see the words it came from, and vice versa.'}
                            </Typography.Text>
                        </div>
                    </Splitter.Panel>
                    <Splitter.Panel style={{ flex: 2, minWidth: '45%' }}>
                        <div
                            style={{
                                margin: '0 12px',
                                height: '100%',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* Above the scroll container, not inside it. Four tiers rendered in
                                full make a panel long enough that Guidance and Needs review are
                                routinely below the fold — including the promotion affordance —
                                so a strip that scrolled away with them would not solve the
                                problem it exists for. Doubles as the legend the drawer otherwise
                                lacks: nothing else here says which colour in the text means
                                which tier. */}
                            {compiled && compileState === 'done' && (
                                <div className={getClassNames('bvd-tier-strip')}>
                                    <BrandVoiceSummaryChips
                                        compiled={compiled}
                                        activeTier={null}
                                        onTierToggle={scrollToTier}
                                    />
                                </div>
                            )}
                            {/* The overlay wraps the SCROLL CONTAINER, not the rules inside it.
                                Wrapping the content meant antd centred the indicator on the
                                content's full scroll height, so on a long decomposition it sat
                                far below the fold — a loading state you have to scroll to find.
                                Anchored here, the box is the visible area and centring means what
                                it looks like it means. */}
                            <Spin
                                spinning={busyMode === 'blocking'}
                                wrapperClassName={getClassNames('bvd-overlay')}
                                // antd dims the wrapped content and sets `pointer-events: none` on
                                // it — exactly the "visible but not actionable" state wanted, from
                                // the design system rather than a hand-rolled scrim.
                                indicator={<AiCompilingIndicator active={busyMode === 'blocking'} />}
                            >
                                <div ref={decompositionRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                    <BrandVoiceDecomposition
                                        state={compileState}
                                        compiled={compiled}
                                        refreshing={busyMode === 'quiet'}
                                        onRetry={() => runCompile(body.trim())}
                                        activeSpanKey={activeSpanKey}
                                        matchedSpanKeys={matchedSpanKeys}
                                        onSpanHover={setHoveredSpanKey}
                                        onSpanClick={handleSpanClick}
                                        suggestionsByItem={suggestionsByItem}
                                        onPromote={(group) =>
                                            setPromotionTarget({
                                                suggestions: group,
                                                p90ByAttribute: contentStats.p90ByAttribute,
                                                p50ByAttribute: contentStats.p50ByAttribute,
                                                sampleSize: contentStats.sampleSize,
                                                enforcedByAttribute,
                                            })
                                        }
                                    />
                                </div>
                            </Spin>
                        </div>
                    </Splitter.Panel>
                </Splitter>
            </Flex>

            <PromotionModal
                target={promotionTarget}
                body={body}
                compiledBody={lastCompiledBodyRef.current}
                onCancel={() => setPromotionTarget(null)}
                onConfirm={handleConfirmPromotion}
            />
        </Drawer>
    )
}

interface IBrandVoiceWell {
    domainId: number
    canEdit: boolean
}

export const BrandVoiceWell: FC<IBrandVoiceWell> = (props) => {
    const directiveSvc = useService(AiGenerationDirectiveService)

    const [loading, setLoading] = useState(true)
    const [directive, setDirective] = useState<AiGenerationDirective | null>(null)
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')

    const [showFullText, setShowFullText] = useState(false)

    // `cancelled` is checked after the await: switching domains starts a second fetch while the
    // first is still open, and without this the slower response wins whichever domain it belongs
    // to — showing one domain's Brand Voice under another's heading.
    const loadBrandVoice = async (isCancelled: () => boolean = () => false) => {
        setLoading(true)
        const res = await directiveSvc.fetchBrandVoice(props.domainId)
        if (isCancelled()) return
        if (res.ok) {
            setDirective(res.data ?? null)
        }
        setLoading(false)
    }

    useEffect(() => {
        let cancelled = false
        loadBrandVoice(() => cancelled)
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.domainId])

    const body = (directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const compiledRules = directive?.active_version?.compiled_rules ?? null

    return (
        <Well
            className="nested"
            title="Brand Voice"
            actions={
                <Button
                    size="small"
                    onClick={() => {
                        setDrawerKey(randomstring.generate())
                        setShowDrawer(true)
                    }}
                    disabled={!props.canEdit || loading}
                >
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            }
            footerMode="hidden"
        >
            <Skeleton loading={loading} active={true} title={false}>
                {!loading &&
                    (body ? (
                        <BrandVoiceSummary body={body} compiled={compiledRules} />
                    ) : (
                        <Typography.Text type="secondary">
                            No Brand Voice set. AI-generated content will use default behavior.
                        </Typography.Text>
                    ))}
            </Skeleton>

            <BrandVoiceEditor
                key={drawerKey}
                domainId={props.domainId}
                directive={directive}
                visible={showDrawer}
                onClose={() => setShowDrawer(false)}
                onSaved={(updated) => {
                    setDirective(updated)
                    setShowDrawer(false)
                }}
            />
        </Well>
    )
}
